import React, { ComponentType, Suspense, ErrorInfo, useCallback, lazy } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * APB-100: Lazy loading utilities for code splitting and performance optimization
 */

interface LazyLoadingFallbackProps {
  message?: string;
  size?: number;
}

/**
 * Loading fallback component for lazy-loaded components
 */
export const LazyLoadingFallback: React.FC<LazyLoadingFallbackProps> = ({ 
  message = 'Loading...', 
  size = 40 
}) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="200px"
    gap={2}
  >
    <CircularProgress size={size} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

/**
 * Enhanced lazy loading wrapper with error boundary and custom fallback
 */
interface LazyWrapperProps {
  fallback?: React.ComponentType<unknown>;
  errorFallback?: React.ComponentType<{ error: Error; retry: () => void }>;
  children: React.ReactNode;
}

export const LazyWrapper: React.FC<LazyWrapperProps> = ({
  fallback: CustomFallback = LazyLoadingFallback,
  errorFallback: ErrorFallback,
  children
}) => {
  const [hasError, setHasError] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (hasError) {
      console.error('APB-100: Lazy loading error:', error);
    }
  }, [hasError, error]);

  const retry = React.useCallback(() => {
    setHasError(false);
    setError(null);
  }, []);

  if (hasError && ErrorFallback) {
    return <ErrorFallback error={error!} retry={retry} />;
  }

  return (
    <Suspense fallback={<CustomFallback />}>
      {children}
    </Suspense>
  );
};

/**
 * Create a lazy-loaded component with enhanced error handling
 */
export const createLazyComponent = <T extends Record<string, unknown>>(
  importFn: () => Promise<{ default: React.ComponentType<T> }>,
  fallback?: React.ComponentType
): React.ComponentType<T> => {
  const LazyComponent = lazy(importFn);
  
  const WrappedComponent = (props: T) => (
    <Suspense fallback={fallback ? React.createElement(fallback) : <div>Loading...</div>}>
      <LazyComponent {...(props as any)} />
    </Suspense>
  );
  
  return WrappedComponent;
};

/**
 * Preload a lazy component for better UX
 */
export function preloadComponent(importFn: () => Promise<unknown>): Promise<unknown> {
  return importFn().catch(error => {
    console.warn('APB-100: Component preload failed:', error);
    return null;
  });
}

/**
 * Hook for managing component preloading
 */
export function useComponentPreloader() {
  const preloadedComponents = React.useRef(new Set<string>());

  const _preloadOnHover = useCallback((componentKey: string, importFn: () => Promise<unknown>) => {
    if (preloadedComponents.current.has(componentKey)) {
      return Promise.resolve();
    }

    preloadedComponents.current.add(componentKey);
    return preloadComponent(importFn);
  }, []);

  return { _preloadOnHover };
}

/**
 * Default error fallback component
 */
export const DefaultErrorFallback: React.FC<{ error: Error; retry: () => void }> = ({
  error,
  retry
}) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="200px"
    gap={2}
    p={3}
  >
    <Typography variant="h6" color="error">
      Failed to load component
    </Typography>
    <Typography variant="body2" color="text.secondary" textAlign="center">
      {error.message}
    </Typography>
    <button
      onClick={retry}
      style={{
        padding: '8px 16px',
        backgroundColor: '#1976d2',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      Retry
    </button>
  </Box>
);

/**
 * Route-level lazy loading wrapper
 */
interface LazyRouteProps {
  component: React.ComponentType<any>;
  fallback?: React.ComponentType<unknown>;
  preloadOnHover?: boolean;
  children?: React.ReactNode;
}

export const LazyRoute: React.FC<LazyRouteProps> = ({
  component: Component,
  fallback,
  preloadOnHover = false,
  children,
  ...props
}) => {
  return (
    <LazyWrapper fallback={fallback} errorFallback={DefaultErrorFallback}>
      <Component {...props}>
        {children}
      </Component>
    </LazyWrapper>
  );
};

/**
 * Chunk loading performance metrics
 */
export class ChunkLoadingMetrics {
  private static metrics: Map<string, {
    loadTime: number;
    success: boolean;
    timestamp: number;
  }> = new Map();

  static recordChunkLoad(chunkName: string, loadTime: number, success: boolean) {
    this.metrics.set(chunkName, {
      loadTime,
      success,
      timestamp: Date.now()
    });

    // Log performance metrics
    console.log(`APB-100: Chunk "${chunkName}" loaded in ${loadTime}ms - ${success ? 'SUCCESS' : 'FAILED'}`);
  }

  static getMetrics() {
    return Array.from(this.metrics.entries()).map(([chunkName, data]) => ({
      chunkName,
      ...data
    }));
  }

  static getAverageLoadTime(): number {
    const successfulLoads = Array.from(this.metrics.values()).filter(m => m.success);
    if (successfulLoads.length === 0) return 0;
    
    const totalTime = successfulLoads.reduce((sum, m) => sum + m.loadTime, 0);
    return totalTime / successfulLoads.length;
  }
}
